// src/page/TicketNew.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import Select from 'react-select';

// options
import { AREA_OPTIONS } from '../constants/spotOptions';
import {
    USAGE_TYPE_OPTIONS,                // [['1','目視確認'],['2','QR読み取り']]
    TICKET_PACKAGE_CATEGORY_OPTIONS,   // [['0','有料乗車券'],['1','0円乗車券']]
    TICKET_LEVEL_OPTIONS               // ['0','1','2','3']
} from '../constants/ticketOptions';

export default function TicketNew() {
    useAuthGuard();
    const navigate = useNavigate();

    // ---------- helpers ----------
    const pad = (n) => String(n).padStart(2, '0');
    const fmtDate = (d) => `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const parseRangeStr = (rangeStr) => {
        if (!rangeStr || !rangeStr.includes('~')) return [null, null];
        const [a, b] = rangeStr.split('~').map(s => s.trim());
        return [a || null, b || null];
    };
    const parseDateTime = (str) => {
        if (!str) return null;
        const [datePart, timePart] = str.trim().split(' ');
        if (!datePart || !timePart) return null;
        const [yyyy, mm, dd] = datePart.split('/').map(Number);
        const [HH, II] = timePart.split(':').map(Number);
        const d = new Date(yyyy, (mm || 1) - 1, dd || 1, HH || 0, II || 0, 0, 0);
        return isNaN(d) ? null : d;
    };
    const daysBetween = (a, b) => {
        if (!a || !b) return null;
        const ms = Math.abs(b.getTime() - a.getTime());
        const days = Math.round(ms / (1000 * 60 * 60 * 24));
        return Math.max(1, days || 1);
    };

    // validation helpers
    const rangeError = (label, val) => {
        if (!val) return `${label}は必須です`;
        if (!/^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/.test(val)) {
            return `${label}の形式が正しくありません（YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm）`;
        }
        const [a, b] = parseRangeStr(val);
        const d1 = parseDateTime(a), d2 = parseDateTime(b);
        if (!d1 || !d2) return `${label}の形式が正しくありません（YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm）`;
        if (d1 > d2) return `${label}は開始≦終了にしてください`;
        return null;
    };
    const isHHmm = (v) => /^\d{4}$/.test(v || '');

    const scrollToFirstError = (errObj) => {
        const keys = Object.keys(errObj);
        if (!keys.length) return;
        const first = keys[0];
        const node = document.querySelector(`[data-error-key="${first}"]`) || document.querySelector(`[name="${first}"]`);
        if (node?.scrollIntoView) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // ---------- pickers refs ----------
    const validFpRef = useRef(null);
    const salesFpRef = useRef(null);
    const publishFpRef = useRef(null);

    // ---------- main states (package) ----------
    const [packageName, setPackageName] = useState('');
    const [ticketBland, setTicketBland] = useState(''); // UI only (not saved)

    const [ticketPackageType, setTicketPackageType] = useState('1');
    const [ticketPackageCategory, setTicketPackageCategory] = useState('0');
    const [ticketLevel, setTicketLevel] = useState('0');
    const [maxHoldingNumber, setMaxHoldingNumber] = useState('');

    const [howToUse, setHowToUse] = useState('');
    const [termsOfUse, setTermsOfUse] = useState('');
    const [caution, setCaution] = useState('');

    // N日券（UIのみだが voucher.effectiveDays に反映）
    const [ticketInterval, setTicketInterval] = useState('');

    // validity （UI → voucher）
    const [validType, setValidType] = useState('range');
    const [validRange, setValidRange] = useState('');
    const [validDays, setValidDays] = useState('1');
    const [validTime, setValidTime] = useState('2359'); // HHmm 4桁

    // usage (UI only)
    const [ticketUsage, setTicketUsage] = useState(USAGE_TYPE_OPTIONS[0][0]);

    // right column
    const [publishedFlag, setPublishedFlag] = useState('0');
    const [salesRange, setSalesRange] = useState('');
    const [publishRange, setPublishRange] = useState('');
    const [displayOrder, setDisplayOrder] = useState('');

    // price/fee/user cancel
    const [pricePackageAdult, setPricePackageAdult] = useState('');
    const [pricePackageChild, setPricePackageChild] = useState('');
    const [refundFeePerUser, setRefundFeePerUser] = useState('');
    const [cancellationFeePerUser, setCancellationFeePerUser] = useState('');
    const [userCancelable, setUserCancelable] = useState('0');

    // spot select
    const [spotId, setSpotId] = useState('');
    const [spots, setSpots] = useState([]);
    const [loadingSpots, setLoadingSpots] = useState(false);

    const spotOptions = useMemo(
        () => spots.map(s => ({ value: s.spotId, label: s.spotName })),
        [spots]
    );

    // image upload (single)
    const [imageFile, setImageFile] = useState(null);        // File | null
    const [imagePreview, setImagePreview] = useState('');    // object URL
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const setSingleImage = (file) => {
        if (!file) return;
        if (!file.type?.startsWith('image/')) {
            alert('画像ファイルを選択してください');
            return;
        }
        if (imagePreview && imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };
    const handlePickClick = () => fileInputRef.current?.click();
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) setSingleImage(file);
        e.target.value = '';
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) setSingleImage(file);
    };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); };
    const clearImage = () => {
        if (imagePreview && imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
        setImageFile(null);
        setImagePreview('');
    };
    useEffect(() => () => { if (imagePreview && imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview); }, [imagePreview]);

    // meta
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // free-ticket helper
    const isFree = ticketPackageCategory === '1';

    // when "０円乗車券" is selected, force 0s into prices/fees
    useEffect(() => {
        if (isFree) {
            setPricePackageAdult('0');
            setPricePackageChild('0');
            setRefundFeePerUser('0');
            setCancellationFeePerUser('0');
        }
        // no cleanup needed; user can edit again when switching back
    }, [isFree]);
    // ---------- init flatpickr ----------
    useEffect(() => {
        if (!window.flatpickr) return;

        validFpRef.current = window.flatpickr('#valid_period_range', {
            mode: 'range', enableTime: true, time_24hr: true, dateFormat: 'Y/m/d H:i',
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) setValidRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
                if (selectedDates.length === 0) setValidRange('');
            }
        });

        salesFpRef.current = window.flatpickr('#sales_range', {
            mode: 'range', enableTime: true, time_24hr: true, dateFormat: 'Y/m/d H:i',
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) setSalesRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
                if (selectedDates.length === 0) setSalesRange('');
            }
        });

        publishFpRef.current = window.flatpickr('#publish_range', {
            mode: 'range', enableTime: true, time_24hr: true, dateFormat: 'Y/m/d H:i',
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onChange: (selectedDates) => {
                if (selectedDates.length === 2) setPublishRange(`${fmtDate(selectedDates[0])} ~ ${fmtDate(selectedDates[1])}`);
                if (selectedDates.length === 0) setPublishRange('');
            }
        });

        return () => {
            try { validFpRef.current?.destroy(); } catch { }
            try { salesFpRef.current?.destroy(); } catch { }
            try { publishFpRef.current?.destroy(); } catch { }
        };
    }, []);

    // reflect controlled -> picker (and clear when empty)
    useEffect(() => {
        if (!validFpRef.current) return;
        if (validRange && validRange.includes('~')) {
            const [a, b] = parseRangeStr(validRange);
            if (a && b) validFpRef.current.setDate([a, b], true);
        } else {
            try { validFpRef.current.clear(); } catch { }
        }
    }, [validRange]);
    useEffect(() => {
        if (!salesFpRef.current) return;
        if (salesRange && salesRange.includes('~')) {
            const [a, b] = parseRangeStr(salesRange);
            if (a && b) salesFpRef.current.setDate([a, b], true);
        } else {
            try { salesFpRef.current.clear(); } catch { }
        }
    }, [salesRange]);
    useEffect(() => {
        if (!publishFpRef.current) return;
        if (publishRange && publishRange.includes('~')) {
            const [a, b] = parseRangeStr(publishRange);
            if (a && b) publishFpRef.current.setDate([a, b], true);
        } else {
            try { publishFpRef.current.clear(); } catch { }
        }
    }, [publishRange]);

    // ---------- (create) no detail load ----------
    useEffect(() => { setLoading(false); }, []);

    // スポット一覧取得
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingSpots(true);
                const token = localStorage.getItem('access_token');
                const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
                const mapped = list.map(s => ({
                    spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
                    spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
                })).filter(x => x.spotId && x.spotName);
                setSpots(mapped);
            } catch (_) {
                setSpots([]);
            } finally {
                setLoadingSpots(false);
            }
        })();
        return () => ac.abort();
    }, []);

    // --- validation UI ---
    const [errors, setErrors] = useState({});
    const invalid = (k) => (errors[k] ? 'is-invalid' : '');
    const feedback = (k) => (errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null);

    const clearValid = () => { setValidRange(''); try { validFpRef.current?.clear(); } catch { } };
    const clearSales = () => { setSalesRange(''); try { salesFpRef.current?.clear(); } catch { } };
    const clearPublish = () => { setPublishRange(''); try { publishFpRef.current?.clear(); } catch { } };

    // helpers for payload emission
    const strToDB = (s) => (s == null ? '' : String(s));
    const intToDB = (s) => {
        if (s === '' || s == null) return null;
        const v = parseInt(s, 10);
        return Number.isFinite(v) ? v : null;
    };

    // ---- upload image to /v1/file (after create -> path=ticket/<newId>/) ----
    const uploadImageForNew = async (newId) => {
        if (!imageFile) return null;
        setUploading(true);
        try {
            const token = localStorage.getItem('access_token');
            const fd = new FormData();
            fd.append('file', imageFile, imageFile.name);
            fd.append('path', `ticket/${newId}/`);
            const res = await fetch(`${API_BASE_URL}/v1/file`, {
                method: 'POST',
                headers: { Authorization: token ? `Bearer ${token}` : '' },
                body: fd
            });
            if (!res.ok) {
                let msg = `画像アップロードに失敗しました (HTTP ${res.status})`;
                try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
                throw new Error(msg);
            }
            try {
                const j = await res.json();
                return j?.path || j?.result || null;
            } catch {
                return await res.text();
            }
        } finally {
            setUploading(false);
        }
    };

    const savingOrUploading = saving || uploading;

    // ---------- submit (POST) ----------
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (savingOrUploading) return;

        // custom validation
        const err = {};
        if (!packageName.trim()) err.packageName = '乗車券名は必須です';
        if (!spotId) err.spotId = 'スポットは必須です';
        const vr = rangeError('有効期間', validRange);
        if (vr) err.validRange = vr;
        if (!isHHmm(validTime)) err.validTime = '有効時刻は4桁のHHmmで入力してください（例: 2359, 2600 など）';
        const se = rangeError('販売期間', salesRange);
        if (se) err.salesRange = se;
        const pe = rangeError('公開期間', publishRange);
        if (pe) err.publishRange = pe;

        if (!pricePackageAdult && pricePackageAdult !== 0) err.pricePackageAdult = '大人料金は必須です';
        if (!pricePackageChild && pricePackageChild !== 0) err.pricePackageChild = '小人料金は必須です';
        if (!refundFeePerUser && refundFeePerUser !== 0) err.refundFeePerUser = '払い戻し手数料は必須です';
        if (!cancellationFeePerUser && cancellationFeePerUser !== 0) err.cancellationFeePerUser = '取り消し手数料は必須です';

        setErrors(err);
        if (Object.keys(err).length) {
            scrollToFirstError(err);
            return;
        }

        // parse periods
        const [vA, vB] = parseRangeStr(validRange);
        const v1 = parseDateTime(vA);
        const v2 = parseDateTime(vB);

        const [sA, sB] = parseRangeStr(salesRange);
        const s1 = parseDateTime(sA);
        const s2 = parseDateTime(sB);

        const [pA, pB] = parseRangeStr(publishRange);
        const p1 = parseDateTime(pA);
        const p2 = parseDateTime(pB);

        // voucher values
        let effDays = intToDB(ticketInterval);
        if (!effDays) {
            if (validType === 'days') effDays = intToDB(validDays);
            else if (v1 && v2) effDays = daysBetween(v1, v2);
        }

        const basePayload = {
            packageName: packageName.trim(),
            spotId: spotId || undefined,
            ticketPackageCategory: ticketPackageCategory || undefined,
            ticketLevel: intToDB(ticketLevel),
            maxHoldingNumber: intToDB(maxHoldingNumber),
            displayOrder: intToDB(displayOrder),
            howToUse: strToDB(howToUse),
            termsOfUse: strToDB(termsOfUse),
            caution: strToDB(caution),
            pricePackageAdult: intToDB(pricePackageAdult),
            pricePackageChild: intToDB(pricePackageChild),
            refundFeePerUser: intToDB(refundFeePerUser),
            cancellationFeePerUser: intToDB(cancellationFeePerUser),
            userCancelable: userCancelable === '1',
            publishedFlag: publishedFlag === '1',
            ticketBrand: strToDB(ticketBland),
            salesStartDate: s1 ? s1.toISOString() : null,
            salesEndDate: s2 ? s2.toISOString() : null,
            publishedStartDate: p1 ? p1.toISOString() : null,
            publishedEndDate: p2 ? p2.toISOString() : null,
            vouchers: [{
                effectiveTime: String(validTime || '').replace(/\D/g, '').padStart(4, '0'),
                usageType: ticketUsage, // ← t_ticket_voucher.usage_type に紐づけ
                effectivePeriodStartDate: v1 ? v1.toISOString() : null,
                effectivePeriodEndDate: v2 ? v2.toISOString() : null,
                effectiveDays: effDays
            }]
        };

        try {
            setSaving(true);
            const token = localStorage.getItem('access_token');

            // 1) Create the ticket
            const res = await fetch(`${API_BASE_URL}/v1/ticket`, {
                method: 'POST',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(basePayload)
            });
            if (!res.ok) {
                let msg = `登録に失敗しました (HTTP ${res.status})`;
                try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
                throw new Error(msg);
            }
            const j = await res.json().catch(() => ({}));
            const newId = j?.packageId;

            // 2) If image selected, upload then PUT to attach
            if (imageFile && newId) {
                const uploadedImagePath = await uploadImageForNew(newId);
                if (!uploadedImagePath) throw new Error('画像のアップロードに失敗しました。');

                const putPayload = { ...basePayload, ticketImage: uploadedImagePath };
                const res2 = await fetch(`${API_BASE_URL}/v1/ticket/${newId}`, {
                    method: 'PUT',
                    headers: {
                        Authorization: token ? `Bearer ${token}` : '',
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(putPayload)
                });
                if (!res2.ok) {
                    let msg = `画像付き更新に失敗しました (HTTP ${res2.status})`;
                    try { const j2 = await res2.json(); if (j2?.error) msg = j2.error; } catch { }
                    throw new Error(msg);
                }
            }

            alert('登録しました。');
            navigate('/ticket');
        } catch (e) {
            alert(e?.message || '登録に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <>
                <nav className="mb-3" aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item"><a href="/ticket">乗車券管理</a></li>
                        <li className="breadcrumb-item active">新規作成</li>
                    </ol>
                </nav>

                <form className="mb-9" onSubmit={handleSubmit}>
                    <div className="row g-3 flex-between-end mb-5">
                        <div className="col-auto">
                            <h2 className="mb-2">乗車券登録</h2>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0" type="button"
                                onClick={() => window.history.back()} disabled={savingOrUploading}>キャンセル</button>
                            <button className="btn btn-primary me-2 mb-2 mb-sm-0" type="submit" disabled={savingOrUploading}>
                                {uploading ? '画像アップロード中...' : (saving ? '登録中...' : '登録')}
                            </button>
                        </div>
                    </div>

                    <div className="row g-5">
                        {/* left */}
                        <div className="col-12 col-xl-8">

                            {/* 基本 */}
                            <div className={`mb-4 ${invalid('packageName')}`} data-error-key="packageName">
                                <h4 className="mb-3">乗車券名<span className="text-danger">*</span></h4>
                                <input className={`form-control ${invalid('packageName')}`} type="text" value={packageName}
                                    onChange={(e) => setPackageName(e.target.value)} disabled={savingOrUploading} />
                                {feedback('packageName')}
                            </div>

                            <div className="mb-4">
                                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                                <Select
                                    options={spotOptions}
                                    value={spotOptions.find(o => o.value === spotId) || null}
                                    onChange={(opt) => setSpotId(opt?.value || '')}
                                    isDisabled={loadingSpots || savingOrUploading}
                                    isClearable
                                    placeholder={loadingSpots ? '読み込み中…' : '選択または検索'}
                                    classNamePrefix="react-select"
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    filterOption={(opt, input) =>
                                        opt.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                                {feedback('spotId')}
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">券種</h4>
                                        <div className="input-group">
                                            <input className="form-control" type="text" name="ticket_bland"
                                                value={ticketBland} onChange={(e) => setTicketBland(e.target.value)} disabled={savingOrUploading} />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">N日券</h4>
                                        <div className="input-group">
                                            <input className="form-control" type="number" min="1"
                                                value={ticketInterval} onChange={(e) => setTicketInterval(e.target.value)} disabled={savingOrUploading} />
                                            <span className="input-group-text">日</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 有効期間 & 有効時刻（voucher 連動） */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className={`mb-4 ${invalid('validRange')}`} data-error-key="validRange">
                                        <h4 className="mb-3">有効期間<span className="text-danger">*</span></h4>
                                        <div className="input-group">
                                            <input className={`form-control ${invalid('validRange')}`} type="text" id="valid_period_range"
                                                placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm" value={validRange} readOnly />
                                            <span className="input-group-text" role="button" tabIndex={0}
                                                onClick={clearValid}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearValid(); }}
                                                aria-label="クリア" title="クリア" style={{ cursor: 'pointer', userSelect: 'none' }}>×</span>
                                        </div>
                                        {feedback('validRange')}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className={`mb-4 ${invalid('validTime')}`} data-error-key="validTime">
                                        <h4 className="mb-3">有効時刻<span className="text-danger">*</span></h4>
                                        <input className={`form-control ${invalid('validTime')}`} type="text" inputMode="numeric" maxLength={4}
                                            placeholder="HHmm 例: 2359 / 2600" value={validTime}
                                            onChange={(e) => {
                                                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                                setValidTime(v);
                                                if (errors.validTime) {
                                                    const next = { ...errors };
                                                    if (isHHmm(v)) delete next.validTime;
                                                    setErrors(next);
                                                }
                                            }} disabled={savingOrUploading} />
                                        {feedback('validTime')}
                                    </div>
                                </div>
                            </div>

                            {/* テキスト群 */}
                            <div className="mb-4">
                                <h4 className="mb-3">使用方法</h4>
                                <textarea className="form-control" rows={4}
                                    value={howToUse} onChange={(e) => setHowToUse(e.target.value)} disabled={savingOrUploading} />
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">利用条件</h4>
                                <textarea className="form-control" rows={4}
                                    value={termsOfUse} onChange={(e) => setTermsOfUse(e.target.value)} disabled={savingOrUploading} />
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">注意事項</h4>
                                <textarea className="form-control" rows={4}
                                    value={caution} onChange={(e) => setCaution(e.target.value)} disabled={savingOrUploading} />
                            </div>

                            {/* 区分/レベル/枚数 */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">チケット利用区分</h4>
                                        <div className="d-flex align-items-center gap-3">
                                            {USAGE_TYPE_OPTIONS.map(([v, label]) => (
                                                <label className="mb-0" key={v}>
                                                    <input type="radio" name="ticket_usage" value={v}
                                                        checked={ticketUsage === v} onChange={(e) => setTicketUsage(e.target.value)} disabled={savingOrUploading} /> {label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 col-md-6">
                                    <h4 className="mb-3">乗車券区分</h4>
                                    <div className="d-flex align-items-center gap-3">
                                        {TICKET_PACKAGE_CATEGORY_OPTIONS.map(([v, label]) => (
                                            <label className="mb-0" key={v}>
                                                <input type="radio" name="ticket_price" value={v}
                                                    checked={ticketPackageCategory === v}
                                                    onChange={(e) => setTicketPackageCategory(e.target.value)} disabled={savingOrUploading} /> {label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">チケットレベル</h4>
                                        <div className="d-flex align-items-center gap-3">
                                            {TICKET_LEVEL_OPTIONS.map((lv) => (
                                                <label className="mb-0" key={lv}>
                                                    <input type="radio" name="ticket_level" value={lv}
                                                        checked={ticketLevel === lv}
                                                        onChange={(e) => setTicketLevel(e.target.value)} disabled={savingOrUploading} /> {lv}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 col-md-6">
                                    <h4 className="mb-3">一人当たり保持（購入）可能枚数</h4>
                                    <input className="form-control" type="number" value={maxHoldingNumber}
                                        onChange={(e) => setMaxHoldingNumber(e.target.value)} disabled={savingOrUploading} />
                                </div>
                            </div>

                            {/* ================= パッケージ販売価格 ================= */}
                            <section className="mt-5">
                                <h2 className="mb-2">パッケージ販売価格</h2>
                                <br />
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className={`mb-4 ${invalid('pricePackageAdult')}`} data-error-key="pricePackageAdult">
                                            <h4 className="mb-3">大人<span className="text-danger">*</span></h4>
                                            <div className="input-group">
                                                <input className={`form-control ${invalid('pricePackageAdult')}`} type="number" min="0"
                                                    value={pricePackageAdult}
                                                    onChange={(e) => setPricePackageAdult(e.target.value)}
                                                    disabled={savingOrUploading || isFree}
                                                />
                                                <span className="input-group-text">円</span>
                                            </div>
                                            {feedback('pricePackageAdult')}
                                        </div>
                                    </div>
                                    <div className={`mb-4 col-md-6 ${invalid('pricePackageChild')}`} data-error-key="pricePackageChild">
                                        <h4 className="mb-3">小人<span className="text-danger">*</span></h4>
                                        <div className="input-group">
                                            <input className={`form-control ${invalid('pricePackageChild')}`} type="number" min="0"
                                                value={pricePackageChild}
                                                onChange={(e) => setPricePackageChild(e.target.value)}
                                                disabled={savingOrUploading || isFree}
                                            />
                                            <span className="input-group-text">円</span>
                                        </div>
                                        {feedback('pricePackageChild')}
                                    </div>
                                </div>

                                <div className="row">
                                    <div className={`col-md-6 ${invalid('refundFeePerUser')}`} data-error-key="refundFeePerUser">
                                        <div className="mb-4">
                                            <h4 className="mb-3">払い戻し手数料<span className="text-danger">*</span></h4>
                                            <div className="input-group">
                                                <input className={`form-control ${invalid('refundFeePerUser')}`} type="number" min="0"
                                                    value={refundFeePerUser}
                                                    onChange={(e) => setRefundFeePerUser(e.target.value)}
                                                    disabled={savingOrUploading || isFree}
                                                />
                                                <span className="input-group-text">円</span>
                                            </div>
                                            {feedback('refundFeePerUser')}
                                        </div>
                                    </div>
                                    <div className={`mb-4 col-md-6 ${invalid('cancellationFeePerUser')}`} data-error-key="cancellationFeePerUser">
                                        <h4 className="mb-3">取り消し手数料<span className="text-danger">*</span></h4>
                                        <div className="input-group">
                                            <input className={`form-control ${invalid('cancellationFeePerUser')}`} type="number" min="0"
                                                value={cancellationFeePerUser}
                                                onChange={(e) => setCancellationFeePerUser(e.target.value)}
                                                disabled={savingOrUploading || isFree}
                                            />
                                            <span className="input-group-text">円</span>
                                        </div>
                                        {feedback('cancellationFeePerUser')}
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <h4 className="mb-3">ユーザーキャンセルフラグ</h4>
                                            <div className="d-flex align-items-center gap-3">
                                                <label className="mb-0">
                                                    <input type="radio" name="ticket_user_cancel_flag" value="0"
                                                        checked={userCancelable === '0'} onChange={() => setUserCancelable('0')} disabled={savingOrUploading} /> 否
                                                </label>
                                                <label className="mb-0">
                                                    <input type="radio" name="ticket_user_cancel_flag" value="1"
                                                        checked={userCancelable === '1'} onChange={() => setUserCancelable('1')} disabled={savingOrUploading} /> 可
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mb-4 col-md-6">{/* 空き */}</div>
                                </div>
                            </section>

                        </div>

                        {/* right */}
                        <div className="col-12 col-xl-4">
                            <div className="row g-2">
                                <div className="col-12 col-xl-12">
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row gx-3">
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input type="radio" name="publish_status" value="0"
                                                                    checked={publishedFlag === '0'} onChange={() => setPublishedFlag('0')} disabled={savingOrUploading} /> 非公開
                                                            </label>
                                                            <label className="mb-0">
                                                                <input type="radio" name="publish_status" value="1"
                                                                    checked={publishedFlag === '1'} onChange={() => setPublishedFlag('1')} disabled={savingOrUploading} /> 公開
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={`col-12 col-sm-6 col-xl-12 ${invalid('publishRange') ? 'is-invalid' : ''}`} data-error-key="publishRange">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                                                        </div>
                                                        <div className="input-group">
                                                            <input className={`form-control ${invalid('publishRange')}`} type="text" id="publish_range" placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm" value={publishRange} readOnly />
                                                            <span className="input-group-text" role="button" tabIndex={0}
                                                                onClick={clearPublish}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearPublish(); }}
                                                                aria-label="クリア" title="クリア" style={{ cursor: 'pointer', userSelect: 'none' }}>×</span>
                                                        </div>
                                                        {feedback('publishRange')}
                                                    </div>
                                                </div>

                                                <div className={`col-12 col-sm-6 col-xl-12 ${invalid('salesRange') ? 'is-invalid' : ''}`} data-error-key="salesRange">
                                                    <div className={`mb-4 ${invalid('salesRange')}`}>
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">販売期間<span className="text-danger">*</span></h5>
                                                        </div>
                                                        <div className="input-group">
                                                            <input className={`form-control ${invalid('salesRange')}`} type="text" id="sales_range" placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm" value={salesRange} readOnly />
                                                            <span className="input-group-text" role="button" tabIndex={0}
                                                                onClick={clearSales}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clearSales(); }}
                                                                aria-label="クリア" title="クリア" style={{ cursor: 'pointer', userSelect: 'none' }}>×</span>
                                                        </div>
                                                        {feedback('salesRange')}
                                                    </div>
                                                </div>

                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                                                        </div>
                                                        <input className="form-control" type="number" value={displayOrder}
                                                            onChange={(e) => setDisplayOrder(e.target.value)} disabled={savingOrUploading} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* image card (single, like Spot/TicketEdit) */}
                                <div className="col-12 col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <h4 className="card-title mb-3">乗車券画像</h4>

                                            {!imagePreview && (
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={handlePickClick}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePickClick(); }}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    onDragEnter={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    className={`border border-dashed rounded-3 p-4 text-center ${isDragOver ? 'bg-body-tertiary' : ''}`}
                                                    style={{ cursor: 'pointer' }}
                                                    aria-label="画像をドラッグ&ドロップ、またはクリックして選択"
                                                >
                                                    <p className="mb-2">ここに画像をドラッグ＆ドロップ</p>
                                                    <p className="mb-2">または</p>
                                                    <button type="button" className="btn btn-outline-primary" disabled={savingOrUploading}>ファイルを選択</button>
                                                    <p className="text-body-tertiary mt-2 mb-0">対応形式: 画像ファイル（1枚まで）</p>
                                                </div>
                                            )}

                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                style={{ display: 'none' }}
                                                disabled={savingOrUploading}
                                            />

                                            {imagePreview && (
                                                <div className="d-flex align-items-start gap-3">
                                                    <img src={imagePreview} alt="プレビュー" className="rounded border" style={{ width: 160, height: 160, objectFit: 'cover' }} />
                                                    <div className="flex-grow-1">
                                                        <div className="mb-2 small text-body-secondary">{imageFile?.name} {imageFile ? `(${Math.round(imageFile.size / 1024)} KB)` : ''}</div>
                                                        <div className="d-flex gap-2">
                                                            <button type="button" className="btn btn-outline-secondary" onClick={handlePickClick} disabled={savingOrUploading}>画像を変更</button>
                                                            <button type="button" className="btn btn-outline-danger" onClick={clearImage} disabled={savingOrUploading}>画像を削除</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {loading && <div className="text-body-secondary mt-3">読み込み中...</div>}
                                            {!loading && error && <div className="text-danger mt-3">{error}</div>}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <input type="hidden" value={ticketPackageType} readOnly />

                </form>
            </>
        </Layout>
    );
}
